/**
 * @module pageFindingMethod
 * @description Utilities for determining how a user arrived at a page for analytics tracking
 */

/**
 * Page finding method constants based on the requirements
 */
export const PAGE_FINDING_METHODS = Object.freeze({
  ACCOUNT_FLYOUT: 'navigation - account flyout',
  ORDER_DETAILS: 'navigation - order details',
  FOOTER: 'navigation - footer',
  EXTERNAL_CAMPAIGN: 'external campaign',
});

/**
 * @function getPageFindingMethod
 * @description Determines how the user arrived at the current page based on URL parameters
 * @param {URLSearchParams|string} [searchParams] - URL search parameters (optional, defaults to current window location)
 * @returns {string} The page finding method for analytics tracking
 *
 */
export const getPageFindingMethod = (searchParams) => {
  let params;

  // Handle different input types
  if (searchParams) {
    if (typeof searchParams === 'string') {
      params = new URLSearchParams(searchParams);
    } else if (searchParams instanceof URLSearchParams) {
      params = searchParams;
    } else {
      params = new URLSearchParams(window?.location?.search || '');
    }
  } else {
    params = new URLSearchParams(window?.location?.search || '');
  }

  // Check for explicit pagefm parameter first
  const pagefm = params.get('pagefm');
  if (pagefm) {
    const decodedPagefm = decodeURIComponent(pagefm).toLowerCase();

    if (decodedPagefm.includes('account flyout')) {
      return PAGE_FINDING_METHODS.ACCOUNT_FLYOUT;
    }
    if (decodedPagefm.includes('order details')) {
      return PAGE_FINDING_METHODS.ORDER_DETAILS;
    }
    if (decodedPagefm.includes('footer')) {
      return PAGE_FINDING_METHODS.FOOTER;
    }
  }

  // Check for external campaign indicators
  const campaignParams = ['utm_source', 'utm_campaign', 'utm_medium', 'referral_code', 'campaignId'];
  const hasExternalCampaignParams = campaignParams.some((param) => params.has(param));

  if (hasExternalCampaignParams) {
    return PAGE_FINDING_METHODS.EXTERNAL_CAMPAIGN;
  }

  // Check for order history context
  const selected = params.get('selected');
  if (selected === 'history') {
    return PAGE_FINDING_METHODS.ORDER_DETAILS;
  }

  // Default fallback - if we can't determine, assume account flyout
  return PAGE_FINDING_METHODS.ACCOUNT_FLYOUT;
};

/**
 * @function setPageFindingMethodParam
 * @description Adds or updates the pagefm parameter in the current URL
 * @param {string} method - The page finding method to set
 *
 */
export const setPageFindingMethodParam = (method) => {
  if (!window?.location || !window?.history) return;

  const url = new URL(window.location.href);
  url.searchParams.set('pagefm', method);

  // Update the URL without triggering a page refresh
  window.history.replaceState(null, '', url.toString());
};
